import { onError } from '@apollo/client/link/error'
import { deleteCookie } from 'cookies-next'
import { captureException } from '~/lib/sentry/captureException'
import { getStatusCodeFromGraphQLErrors } from '~/utils/statusCode'
import { color, Toast } from '@ticketswap/solar'
import { CloseRounded } from '@ticketswap/solar/icons'
import { Notify } from '.'
import { Translate } from '~/types/Translation'

const filterErrors = ({ message }: { message: string }) =>
  !['Invalid or expired login link'].includes(message)

const errorHandlerLink = (notify?: Notify, t?: Translate) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    if (notify) {
      notify(() => (
        <Toast leftAdornment={<CloseRounded color={color.failure} size={24} />}>
          {t ? t('common:error.clientError') : 'Something went wrong'}
        </Toast>
      ))
    }

    if (graphQLErrors) {
      const statusCode = getStatusCodeFromGraphQLErrors(graphQLErrors)

      if (statusCode === 401) {
        /**
         * If user is unauthorized when doing a query it usually means that the token is present but
         * has expired (tokens lifetime is 30 days). If this is the case we want to remove the token
         * and update the cache accordingly.
         *
         * TODO: Use a refresh token here to update the token
         */
        if (process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME) {
          deleteCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME)
        }

        const { cache } = operation.getContext()
        cache.reset()
      }

      graphQLErrors.filter(filterErrors).forEach(graphqlError => {
        captureException(new Error(graphqlError.message), 'graphql', {
          extra: {
            graphqlError,
            operation: operation.operationName,
            variables: operation.variables,
          },
        })
      })
    }

    if (networkError) {
      if (!('result' in networkError)) {
        console.error('API error:', networkError)

        return captureException(new Error(`API error: ${networkError.message}`), 'graphql', {
          extra: {
            operation: operation.operationName,
            variables: operation.variables,
            networkError,
          },
        })
      }

      const errorDetails = {
        ...networkError.result,
      }

      console.error('API error:', errorDetails)

      captureException(new Error(`API error: ${networkError.result.detail}`), 'graphql', {
        extra: {
          operation: operation.operationName,
          variables: operation.variables,
          errorDetails,
        },
      })
    }
  })

export default errorHandlerLink
